import { getRouteApi } from '@tanstack/react-router'
import type { TFunction } from 'i18next'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import EditIcon from '@/assets/icons/edit.svg?react'
import Button from '@/components/Button/Button'
import DataBlock from '@/components/DataBlock/DataBlock'
import type { LabelAndValue } from '@/types/label-and-value'

import styles from './SchoolDayDetailsView.module.scss'
import SchoolSettingsView from './SchoolSettingsView'
import SchoolDayPlanAlert from '../components/SchoolDayPlanAlert'
import SchoolDayPreview from '../components/SchoolDayPreview'
import type { TimetableSettingsCustomBreak } from '../queries/timetableSettingsQueryOptions'
import { formatTimeDuration } from '../utils/duration'

const routeApi = getRouteApi('/_auth/school-settings/day/')

const SchoolDayDetailsView = () => {
  const { t } = useTranslation('schoolSettings')

  const timetableSettings = routeApi.useLoaderData()

  const lessonInformation = [
    { label: t('label.lessons-start-at'), value: timetableSettings.startHour },
    {
      label: t('label.lesson-duration'),
      value: formatTimeDuration(timetableSettings.slotDuration)
    },
    {
      label: t('label.maximum-lessons-per-day'),
      value: timetableSettings.numOfSlots
    }
  ]

  const breakInformation = useMemo(
    () => [
      {
        label: t('label.regular-break-duration'),
        value: formatTimeDuration(timetableSettings.breakDuration)
      },
      ...getSpecialBreaksInfoArray(timetableSettings.customBreaks, t)
    ],
    [t, timetableSettings.breakDuration, timetableSettings.customBreaks]
  )

  return (
    <SchoolSettingsView
      actions={[
        {
          icon: <EditIcon />,
          text: t('button.edit'),
          asLink: true,
          to: '/school-settings/day/edit'
        }
      ]}
      contentClassName={styles.main}
    >
      {timetableSettings.shouldDisplayRegeneratedInfo ? (
        <SchoolDayPlanAlert />
      ) : null}
      <div className={styles.content}>
        <h2 className={styles.title}>
          {t('header.details')}
          <Button
            variant="secondary"
            size="small"
            asLink
            to="/school-settings/day/edit"
            icon={<EditIcon />}
          >
            {t('button.edit')}
          </Button>
        </h2>
        <div className={styles.blocks}>
          <DataBlock
            header={t('header.lesson-information')}
            infoArray={lessonInformation}
          />
          <DataBlock
            header={t('header.break-information')}
            infoArray={breakInformation}
          />
        </div>
      </div>

      <div className={styles.content}>
        <h2 className={styles.title}>{t('header.school-day')}</h2>
        <div className={styles.blocks}>
          <SchoolDayPreview timetableSettings={timetableSettings} />
        </div>
      </div>
    </SchoolSettingsView>
  )
}

const getSpecialBreaksInfoArray = (
  customBreaks: TimetableSettingsCustomBreak[],
  t: TFunction<'schoolSettings'>
): LabelAndValue[] =>
  customBreaks.map((customBreak, index) => ({
    label: t('label.special-break-n', { ORDINAL: index + 1 }),
    value: [
      formatTimeDuration(customBreak.duration),
      ...customBreak.slots.map(slot =>
        t('label.after-lesson-n', { ORDINAL: slot + 1 })
      )
    ].join(', ')
  }))

export default SchoolDayDetailsView
